/**
 * The hub's re-run choices for a paired comparison (spec §12.9): "Re-run
 * both", "Re-run cut & fill only", "Re-run deviation map only". Pure — the
 * gates and flags are resolved by the caller (`useComparisonRerun`).
 *
 * They follow the Compare surfaces dialog's own rules, so a choice never
 * promises a result the dialog will not tick:
 *  - the entry gate (`analysisGate('compare')`, via availability) hides or
 *    locks all of them — the re-run goes through that gate;
 *  - a card whose PostHog flag is explicitly off hides every choice that
 *    ticks it (an unloaded flag does not, as in the dialog);
 *  - a locked deviation gate locks every choice that ticks the deviation
 *    map: muted, still clickable, the click opens the upgrade dialog.
 */
import type { FeatureGate } from '@/lib/featureGates';

import type { CompareResult } from './compare/availability';

export type RerunChoiceKey = 'both' | CompareResult;

export interface RerunLock {
  gate: FeatureGate;
  promptUpgrade: () => void;
}

export interface RerunChoice {
  key: RerunChoiceKey;
  /** The results the dialog opens with ticked. */
  results: CompareResult[];
  labelKey: string;
  /** Set when a gate is closed: show the choice muted with a badge; a click
   *  calls `promptUpgrade` instead of re-running. */
  lock: RerunLock | null;
}

const CHOICES: { key: RerunChoiceKey; results: CompareResult[] }[] = [
  { key: 'both', results: ['cutFill', 'deviation'] },
  { key: 'cutFill', results: ['cutFill'] },
  { key: 'deviation', results: ['deviation'] },
];

export function comparisonRerunChoices({
  visible,
  cutFillLock,
  deviationLock,
  flags,
}: {
  /** Compare surfaces is visible to this user (flag, role, not a shared link). */
  visible: boolean;
  /** The entry gate, when closed. */
  cutFillLock: RerunLock | null;
  /** The deviation card's gate, when closed. */
  deviationLock: RerunLock | null;
  flags: Record<CompareResult, boolean | undefined>;
}): RerunChoice[] {
  if (!visible) return [];
  const shown = (r: CompareResult) => flags[r] !== false;
  return CHOICES.filter((c) => c.results.every(shown)).map((c) => ({
    ...c,
    labelKey: `analyses.rerunChoice.${c.key}`,
    lock: cutFillLock ?? (c.results.includes('deviation') ? deviationLock : null),
  }));
}
